// src/subscreens/customer/biddingDetails/BiddingDetails.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useContext } from "react";
import AppContext from "../../../context/AppContext";
import Modal from "../../../components/customer/modal/Modal";
import BidHistory from "../../../components/customer/bidHistory/bidHistory";
import {
  Trophy,
  MapPin,
  Users,
  Eye,
  CalendarDays,
  AlertTriangle,
  Info,
  CheckCircle,
} from "lucide-react";
import "../../../assets/css/customer/biddingDetails/BiddingDetails.css";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=600&fit=crop&auto=format";

const FURNITURE_IMAGES = [
  "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=600&fit=crop&auto=format",
  "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=800&h=600&fit=crop&auto=format",
  "https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=800&h=600&fit=crop&auto=format",
  "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop&auto=format",
  "https://images.unsplash.com/photo-1540574163026-643ea20ade25?w=800&h=600&fit=crop&auto=format",
];

// mode: "browse" = full detail with place bid
//       "placed" = detail with history but no place bid
//       "posted" = only item info, no history, no place bid
const BiddingDetails = ({ bidding, isOpen, onClose, onBidPlaced, mode = "browse" }) => {
  const { baseUrl, baseUrlForFile, loggedInCustomer } = useContext(AppContext);
  const CURRENT_USER_ID = loggedInCustomer?.id || null;

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [liveBidding, setLiveBidding] = useState(bidding);

  const isOwnBidding = liveBidding.adcustomer_id === CURRENT_USER_ID;

  // ✅ FIX: bid records use "adcustomer_id" (from Adplacebid model), not "bidderId"
  const getIsWinning = (bids = []) => {
    const winning = bids.find((b) => b.status === "winning");
    return winning?.adcustomer_id === CURRENT_USER_ID;
  };

  const getCurrentUserBid = (bids = []) =>
    bids.find((b) => b.adcustomer_id === CURRENT_USER_ID) || null;

  // ✅ FIX: read from "adplacebids" (the actual relation returned by backend),
  // not "bids" which never existed on the bidding object
  const [isWinning, setIsWinning] = useState(getIsWinning(liveBidding.adplacebids));
  const [userBid, setUserBid] = useState(getCurrentUserBid(liveBidding.adplacebids));

  const minimumBid = liveBidding.currentBid + liveBidding.minBidIncrement;
  const [bidAmount, setBidAmount] = useState(minimumBid);
  const [bidError, setBidError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bidSuccess, setBidSuccess] = useState(false);
  const [lastPlacedAmount, setLastPlacedAmount] = useState(null);

  const getTimeRemaining = () => {
    const now = new Date();
    const end = new Date(liveBidding.endDate);
    const diff = end - now;
    if (diff <= 0) return { expired: true };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000),
      expired: false,
    };
  };

  const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining());

  useEffect(() => {
    const timer = setInterval(() => setTimeRemaining(getTimeRemaining()), 1000);
    return () => clearInterval(timer);
  }, [liveBidding.endDate]);

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0,
    }).format(price);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleString("en-PK", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  // ✅ FIX: refetch the fresh bidding (with its adplacebids relation) from backend
  // after a bid is placed, instead of manually reconstructing a "bids" array
  // that BidHistory never actually reads from.
  const refetchBidding = () => {
    const authToken = localStorage.getItem("authToken");
    return axios
      .get(`${baseUrl}/customer/bidding/getbidding/${liveBidding.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const fresh = res.data.bidding;
        setLiveBidding(fresh);
        setIsWinning(getIsWinning(fresh.adplacebids));
        setUserBid(getCurrentUserBid(fresh.adplacebids));
        setBidAmount(fresh.currentBid + fresh.minBidIncrement);
      });
  };

  const handlePlaceBid = () => {
    const authToken = localStorage.getItem("authToken");
    if (bidAmount < minimumBid) {
      setBidError(`Minimum bid is ${formatPrice(minimumBid)}`);
      return;
    }
    setIsSubmitting(true);
    const placedAmount = bidAmount;

    axios.post(
      `${baseUrl}/customer/bidding/placebid`,
      { adbidding_id: liveBidding.id, bidamount: bidAmount },
      { headers: { Authorization: `Bearer ${authToken}` } }
    )
      .then(() => {
        return refetchBidding().then(() => {
          setLastPlacedAmount(placedAmount);
          setBidSuccess(true);
          setTimeout(() => setBidSuccess(false), 3000);
          if (onBidPlaced) onBidPlaced();
        });
      })
      .catch((err) => {
        setBidError(err.response?.data?.message || "Failed to place bid. Please try again.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleBidChange = (e) => {
    setBidAmount(Number(e.target.value));
    setBidError("");
  };

  const displayImages =
    liveBidding.adcustomerbidsimage?.length > 0
      ? liveBidding.adcustomerbidsimage.map((img) => `${baseUrlForFile}/${img.bidImages}`)
      : FURNITURE_IMAGES;
  const productImage = displayImages[selectedImageIndex] || FALLBACK_IMAGE;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="extra-large">
      <div className="biddetails-container">

        {/* ── Left Column ── */}
        <div className="biddetails-left">
          <div className="biddetails-main-image">
            <img
              src={productImage}
              alt={liveBidding.productName}
              className="biddetails-image"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://picsum.photos/seed/${String(liveBidding.id).slice(0, 6)}/800/600`;
              }}
            />
            {isWinning && mode !== "posted" && (
              <div className="biddetails-winning-badge">
                <Trophy size={15} />
                You're Winning!
              </div>
            )}
            {liveBidding.status === "ended" && (
              <div className="biddetails-ended-badge">Bidding Ended</div>
            )}
          </div>

          <div className="biddetails-thumbnails">
            {displayImages.map((img, index) => (
              <button
                key={index}
                className={`biddetails-thumbnail ${selectedImageIndex === index ? "active" : ""}`}
                onClick={() => setSelectedImageIndex(index)}
              >
                <img
                  src={img}
                  alt={`view ${index + 1}`}
                  className="biddetails-thumbnail-img"
                  onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMAGE; }}
                />
              </button>
            ))}
          </div>

          <div className="biddetails-seller-card">
            <h4 className="biddetails-seller-title">Seller Information</h4>
            <div className="biddetails-seller-info">
              <div className="biddetails-seller-row">
                <span className="biddetails-seller-label">Name</span>
                <span className="biddetails-seller-value">{liveBidding.adcustomer?.fullName}</span>
              </div>
              <div className="biddetails-seller-row">
                <span className="biddetails-seller-label"><MapPin size={13} /> Location</span>
                <span className="biddetails-seller-value">{liveBidding.location}</span>
              </div>
              <div className="biddetails-seller-row">
                <span className="biddetails-seller-label">Phone</span>
                <span className="biddetails-seller-value">{liveBidding.phone || "—"}</span>
              </div>
              <div className="biddetails-seller-row">
                <span className="biddetails-seller-label">Email</span>
                <span className="biddetails-seller-value">{liveBidding.adcustomer?.email}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Column ── */}
        <div className="biddetails-right">
          <div className="biddetails-header">
            <div className="biddetails-category-badge">{liveBidding.category}</div>
            <h2 className="biddetails-title">{liveBidding.productName}</h2>
            <div className="biddetails-condition">{liveBidding.condition}</div>
          </div>

          <div className="biddetails-description">
            <h4 className="biddetails-section-title">Description</h4>
            <p className="biddetails-description-text">{liveBidding.description}</p>
          </div>

          {/* ── Price Section ── */}
          <div className="biddetails-price-section">
            <div className="biddetails-price-card">
              <div className="biddetails-current-bid">
                <span className="biddetails-price-label">Current Bid</span>
                <span className="biddetails-price-value">{formatPrice(liveBidding.currentBid)}</span>
              </div>
              <div className="biddetails-starting-price">
                <span className="biddetails-starting-label">Starting Price</span>
                <span className="biddetails-starting-value">{formatPrice(liveBidding.startingPrice)}</span>
              </div>
            </div>

            <div className="biddetails-increment-info">
              Minimum bid increment: {formatPrice(liveBidding.minBidIncrement)}
            </div>

            {/* Place Bid — sirf browse mode mein, ended nahi ho, apni bidding nahi */}
            {mode === "browse" && !isOwnBidding && liveBidding.status === "active" && (
              <>
                {bidSuccess ? (
                  <div className="biddetails-bid-success">
                    <CheckCircle size={18} />
                    Bid of {formatPrice(lastPlacedAmount)} placed successfully!
                  </div>
                ) : (
                  <div className="biddetails-inline-bid">
                    <input
                      type="number"
                      className={`biddetails-bid-input ${bidError ? "error" : ""}`}
                      value={bidAmount}
                      onChange={handleBidChange}
                      min={minimumBid}
                      step={liveBidding.minBidIncrement}
                      disabled={isSubmitting}
                      placeholder={`Min: ${formatPrice(minimumBid)}`}
                    />
                    {bidError && <div className="biddetails-bid-error">{bidError}</div>}
                    <button
                      className="biddetails-place-bid-btn"
                      onClick={handlePlaceBid}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Placing..." : isWinning ? "Increase Your Bid" : "Place Bid"}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Ended message */}
            {liveBidding.status === "ended" && (
              <div className="biddetails-countdown expired">
                <div className="biddetails-countdown-label">Bidding Ended</div>
              </div>
            )}

            {/* Countdown — sirf active mein */}
            {liveBidding.status === "active" && !timeRemaining.expired && (
              <div className="biddetails-countdown">
                <div className="biddetails-countdown-label">Time Remaining</div>
                <div className="biddetails-countdown-timer">
                  {[
                    { value: timeRemaining.days, label: "Days" },
                    { value: timeRemaining.hours, label: "Hours" },
                    { value: timeRemaining.minutes, label: "Min" },
                    { value: timeRemaining.seconds, label: "Sec" },
                  ].map(({ value, label }, i, arr) => (
                    <React.Fragment key={label}>
                      <div className="biddetails-time-unit">
                        <span className="biddetails-time-value">{value}</span>
                        <span className="biddetails-time-label">{label}</span>
                      </div>
                      {i < arr.length - 1 && <div className="biddetails-time-separator">:</div>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* Outbid notice — sirf placed mode mein */}
            {mode === "placed" && userBid && !isWinning && (
              <div className="biddetails-your-bid-status">
                <AlertTriangle size={22} className="biddetails-outbid-icon" />
                <div>
                  <div className="biddetails-outbid-title">You've been outbid!</div>
                  <div className="biddetails-outbid-amount">
                    Your bid: {formatPrice(userBid.bidamount)}
                  </div>
                </div>
              </div>
            )}

            {/* Own bidding notice — sirf posted mode mein */}
            {mode === "posted" && (
              <div className="biddetails-own-bidding-notice">
                <Info size={16} />
                This is your posted item.
              </div>
            )}
          </div>

          {/* ── Stats ── */}
              <div className="biddetails-stat-info">
                <span className="biddetails-stat-value">
                  {formatDate(liveBidding.startDate).split(",")[0]}
                </span>
                <span className="biddetails-stat-label">Started</span>
              </div>

          {/* ── Bid History — posted mode mein nahi dikhega ── */}
          {mode !== "posted" && (
            <div className="biddetails-history-section">
              <h4 className="biddetails-section-title">Bid History</h4>
              <BidHistory bids={liveBidding.adplacebids || []} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BiddingDetails;